import {
    COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
    enhanceOptionsSchemaWithDynamicOptions,
    getPattern,
    LAYOUT_TYPE,
} from '../utils';

export const markerGaugeOptionsSchema = enhanceOptionsSchemaWithDynamicOptions({
    backgroundColor: {
        description:
            'Specify the color used for the background. The default for enterprise light is "#ffffff". The default for enterprise dark is "#000000". The default for prisma dark is "#0b0c0e".',
        type: 'string',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        default: '> themes.defaultBackgroundColor',
    },
    gaugeRanges: {
        description:
            'Specify the overall range of the gauge as a series of continuous, color-coded subranges. For example, [{"from": 0, "to": 20, "value": "#4BEBA8"}, {"from": 20, "to": 50, "value": "#F4DF7A"}].',
        default: [
            { from: 0, to: 50, value: '#118832' },
            { from: 50, to: 90, value: '#CBA700' },
            { from: 90, to: 100, value: '#D41F1F' },
        ],
        type: 'array',
        items: {
            type: 'object',
            properties: {
                from: {
                    type: 'number',
                    description: 'Starting value for range.',
                },
                to: {
                    type: 'number',
                    description: 'Ending value for range.',
                },
                value: {
                    description: 'Color for sub range.',
                    type: 'string',
                    pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
                },
            },
            required: ['from', 'to', 'value'],
            additionalProperties: false,
        },
    },
    labelDisplay: {
        description:
            'Specify whether to display the labels as numbers, percentages, or off. The label center aligns to the value.',
        default: 'number',
        type: 'string',
        pattern: getPattern(['number', 'percentage', 'off']),
    },
    majorTickInterval: {
        description: 'Specify the interval between major tick marks.',
        default: 'auto',
        type: ['string', 'number'],
    },
    orientation: {
        description: 'Specify the axis orientation of the gauge.',
        default: 'vertical',
        type: 'string',
        pattern: getPattern(['horizontal', 'vertical']),
    },
    value: {
        description: 'Specify the dataSource applied to the value of the marker gauge.',
        type: 'string',
        default: '> primary | seriesByType("number") | lastPoint()',
    },
    valueDisplay: {
        description: 'Specify whether to display the value as a number, percentage, or off.',
        default: 'number',
        type: 'string',
        pattern: getPattern(['number', 'percentage', 'off']),
    },

    splitByLayout: {
        default: 'off',
        description:
            'Specify the layout method by which to display the visualization, which splits the data into individual visualizations based on a certain category.',
        type: 'string',
        pattern: getPattern([LAYOUT_TYPE.Off, LAYOUT_TYPE.Trellis]),
        enum: [LAYOUT_TYPE.Off, LAYOUT_TYPE.Trellis],
    },
    trellisBackgroundColor: {
        default: '> themes.defaultBackgroundColor',
        description:
            'Specify the color used for the trellis container background by using a hexadecimal code. For example, #0000FF.',
        pattern: COLOR_OR_TOKEN_PATTERN_WITH_RGBA,
        type: 'string',
    },
    trellisColumns: {
        description:
            'Specify the number of visualizations to display in a given row of the trellis container. The remaining visualizations will wrap accordingly. If nothing is specified, it will be auto-set based on the trellisMinColumnWidth. The minimum value is 1.',
        type: 'number',
        minimum: 1,
        default: 3,
    },
    trellisMinColumnWidth: {
        default: 200,
        minimum: 1,
        description:
            'Specify the minimum width, in pixels, of each visualization in the trellis container. If the window or panel is resized, the remaining visualizations may be viewed by scrolling. The minimum value is 1.',
        type: 'number',
    },
    trellisPageCount: {
        default: 20,
        minimum: 1,
        description:
            'Specify the maximum number of visualizations to display in a single page in the trellis container. The remaining visualizations will paginate accordingly. The minimum value is 1.',
        type: 'number',
    },
    trellisRowHeight: {
        default: 200,
        minimum: 1,
        description:
            'Specify the height, in pixels, of each visualization in the trellis container. If the window or panel is resized, the remaining visualizations may be viewed by scrolling. The minimum value is 1.',
        type: 'number',
    },
    trellisSortBy: {
        default: 'result',
        description:
            'Specify the sort method for the individual visualizations in the trellis container. Use result to sort by search result order, name to sort alphabetically by visualization label, or value to sort by the major value.',
        type: 'string',
        pattern: getPattern(['result', 'name', 'value']),
        enum: ['result', 'name', 'value'],
    },
    trellisSortOrder: {
        default: 'ascending',
        description:
            'Specify the sort order for the individual visualizations in the trellis container. This setting applies to the sort method specified in trellisSortBy.',
        type: 'string',
        pattern: getPattern(['ascending', 'descending']),
        enum: ['ascending', 'descending'],
    },
    trellisSplitBy: {
        description:
            'Specify the field name of the column with categories used, or "aggregations", to split the data into individual visualizations for trellis display, if applicable. If a SPL `timechart` command is used, this may default to the SPL `by` clause field. If a SPL `chart` command is used, this may default to the first string field.',
        type: 'string',
    },
});
